<template>
  <v-row class="mt-0" no-gutters justify="center" v-if="eventData?.event_sponsors?.length">
    <p class="mb-4 mr-auto">Event Brought To You By</p>
    <v-divider class="mb-4" />
    <template v-for="sponsor in eventData.event_sponsors" :key="sponsor.sponsor_id">
      <a
        v-if="sponsor.sponsor_website"
        :href="formatUrl(sponsor.sponsor_website)"
        target="_blank"
        class="pa-2 sponsor-block"
        @pointerdown="handleSponsorClick(sponsor, $event)"
        tabindex="0"
        aria-label="Sponsor Website"
      >
        <div class="sponsor-content">
          <img
            v-if="showLogo(sponsor)"
            :ref="(element) => registerLogo(element, sponsor.sponsor_id)"
            :src="sponsor.sponsor_logo"
            class="sponsor-logo"
            :alt="sponsor.sponsor_name"
            @error="handleLogoError(sponsor.sponsor_id)"
          />
          <div v-else class="sponsor-name-text sponsor-text-logo">{{ sponsor.sponsor_name }}</div>
        </div>
      </a>
      <span v-else class="pa-2 sponsor-block">
        <div class="sponsor-content">
          <img
            v-if="showLogo(sponsor)"
            :ref="(element) => registerLogo(element, sponsor.sponsor_id)"
            :src="sponsor.sponsor_logo"
            class="sponsor-logo"
            :alt="sponsor.sponsor_name"
            @error="handleLogoError(sponsor.sponsor_id)"
          />
          <div v-else class="sponsor-name-text sponsor-text-logo">{{ sponsor.sponsor_name }}</div>
        </div>
      </span>
    </template>
  </v-row>
</template>

<script setup>
import { onMounted } from 'vue';
import BaseUrl from '@/api/BaseUrl';
import Endpoints from '@/api/endpoints';
import Headers from '@/api/Headers';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import useEventDataStore from '@/stores/eventDataStore';
import useSponsorLogos from '@/composables/useSponsorLogos';

const route = useRoute();
const { eventData } = storeToRefs(useEventDataStore());

const { showLogo, handleLogoError, registerLogo } = useSponsorLogos();

const updateSponsorStat = async (sponsorId, type = 'impressions') => {
  const updateSponsorUrl = `${BaseUrl.get(Endpoints.EVENTS.SPONSPOR_PROFILE)}${Endpoints.EVENTS.SPONSPOR_PROFILE}`;
  try {
    await $fetch(updateSponsorUrl, {
      method: 'PUT',
      query: {
        event_id: route.params.id,
        sponsor_id: sponsorId,
      },
      credentials: 'include',
      headers: {
        ...Headers,
      },
      body: { [type]: true },
    });
  } catch (e) {
    console.warn(`Failed to update sponsor ${type}`, sponsorId, e);
  }
};

const handleSponsorClick = async (sponsor, event) => {
  if (!sponsor?.sponsor_website) return;

  if (event?.button === 2) return;

  await updateSponsorStat(sponsor.sponsor_id, 'clicks');
};

const formatUrl = (url) => (url.startsWith('http') ? url : `https://${url}`);

onMounted(() => {
  for (const sponsor of eventData.value?.event_sponsors ?? []) {
    updateSponsorStat(sponsor.sponsor_id, 'impressions');
  }
});
</script>

<style scoped>
.sponsor-block {
  min-width: 140px;
  max-width: 220px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
  text-decoration: none;
}

.sponsor-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.sponsor-logo {
  max-width: 200px;
  width: auto;
  height: auto;
  margin: 0 auto;
  object-fit: contain;
}

.sponsor-name-text {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: justify;
  text-transform: uppercase;
  font-size: 24px;
  line-height: 32px;
  font-weight: 800;
  color: #000;
  max-width: 150px;
  white-space: normal;
  flex-wrap: wrap;
  word-break: break-word;
}

@media (max-width: 600px) {
  .sponsor-block {
    min-width: 100px;
    max-width: 150px;
  }
  .sponsor-name-text {
    font-size: 18px;
    line-height: 28px;
    max-width: 175px;
  }
  .sponsor-logo {
    max-width: 125px;
  }
}
</style>
